import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import { getDatabase, ref, get, set } from "firebase/database";
import { toast } from "../toast";
import { Slate, Editable, withReact } from 'slate-react';
import { createEditor, Editor, Transforms, Element as SlateElement } from 'slate';
import { withHistory } from 'slate-history';
import './eventCreate.css';


const EventCreate = () => {
  const [user, setUser] = useState(null);
  const [editor] = useState(() => withHistory(withReact(createEditor())));
  const [editorValue, setEditorValue] = useState([
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
  ]);

  // Generate a unique EVENT_URLKEY
  const generateEventURLKey = async (database) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const length = 8;
    
    const checkKeyExists = async (key) => {
      try {
        const eventsRef = ref(database, 'EVENTS/EVENT');
        const snapshot = await get(eventsRef);
        
        if (!snapshot.exists()) return false;
        
        const allEvents = snapshot.val();
        
        // Check through all categories and events
        for (const category in allEvents) {
          for (const eventId in allEvents[category]) {
            if (allEvents[category][eventId].EVENT_URLKEY === key) {
              return true;
            }
          }
        }
        return false;
      } catch (error) {
        console.error("Error checking key existence:", error);
        return false;
      }
    };
    
    let key;
    let exists = true;
    
    while (exists) {
      key = '';
      for (let i = 0; i < length; i++) {
        key += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      exists = await checkKeyExists(key);
    }
    
    return key;
  };

  // Toggle block formatting
  const toggleBlock = (format) => {
    const isActive = isBlockActive(editor, format);
    const isList = ['bulleted-list', 'numbered-list'].includes(format);

    Transforms.unwrapNodes(editor, {
      match: n => ['bulleted-list', 'numbered-list'].includes(SlateElement.isElement(n) ? n.type : ''),
      split: true,
    });

    let newProperties;
    if (isList) {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    } else {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    }
    Transforms.setNodes(editor, newProperties);
  };

  // Toggle inline formatting
  const toggleMark = (format) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  };

  const isBlockActive = (editor, format) => {
    const { selection } = editor;
    if (!selection) return false;
    const [match] = Editor.nodes(editor, {
      match: n => SlateElement.isElement(n) && n.type === format,
    });
    return !!match;
  };

  const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  };

  const BlockButton = ({ format, label }) => {
    const isActive = isBlockActive(editor, format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleBlock(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}

        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  const MarkButton = ({ format, label }) => {
    const isActive = isMarkActive(editor, format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleMark(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}
        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  const [category, setCategory] = useState("");  // Event Category
  const [eventType, setEventType] = useState(""); // Event Type

  // Define options for each category
  const eventOptions = {
    AUTOMOTIVE: [
      { value: "RACE", label: "Race" },
      { value: "RALLY", label: "Rally" },
      { value: "DRAG", label: "Drag" },
      { value: "DRIFT", label: "Drift" },
      { value: "TRACKDAY", label: "Trackday" },
      { value: "MOTORBIKE", label: "Motorbike" },
      { value: "MEETUP", label: "Meetup" },
      { value: "OTHER", label: "Other" },
    ],
    SPORTS: [
      { value: "HOCKEY", label: "Hockey" },
      { value: "FOOTBALL", label: "Football" },
      { value: "BASKETBALL", label: "Basketball" },
      { value: "SWIMMING", label: "Swimming" },
      { value: "OTHER", label: "Other" },
    ],
    MUSIC: [
      { value: "FESTIVALS", label: "Festivals" },
      { value: "ROCK", label: "Rock" },
      { value: "INDIE", label: "Indie" },
      { value: "JAZZ", label: "Jazz" },
      { value: "DJ", label: "DJ" },
      { value: "DRUMANDBASS", label: "Drum and Bass" },
      { value: "COUNTRYFOLK", label: "Country/Folk" },
      { value: "METAL", label: "Metal" },
      { value: "POP", label: "Pop" },
      { value: "OTHER", label: "Other" },
    ],
  };

  // Handle category change
  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setEventType(""); // Reset event type when category changes
    setEventTypeError("");
  };


  useEffect(() => {
    // Check for Bootstrap availability
    if (!window.bootstrap) {
      console.error("Bootstrap JS not loaded. Ensure the CDN is included in index.html.");
    }

    // Firebase Auth listener
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const [eventNameError, setEventNameError] = useState("");
  const [eventTypeError, setEventTypeError] = useState("");
  const [eventPriceError, setEventPriceError] = useState("");
  const [eventLocationError, setEventLocationError] = useState("");
  const [eventPostcodeError, setEventPostcodeError] = useState("");

  const [eventDayDateError, setEventDayDateError] = useState("");
  const [eventMonthDateError, setEventMonthDateError] = useState("");
  const [eventYearDateError, setEventYearDateError] = useState("");


  const handleSaveEvent = () => {
    const database = getDatabase();

    onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      const uid = user.uid;
      const userRef = ref(database, "USERS/" + uid);

      try {
        const snapshot = await get(userRef);
        if (!snapshot.exists()) {
          console.error("User data not found.");
          return;
        }

        const userData = snapshot.val();
        const EVENT_OWNERDISPLAY = userData.USERDISPLAYNAME || "Unknown";

        const eventName = document.getElementById("EventName")?.value;
        if (!eventName || eventName.trim() === "") {
          setEventNameError("Event name is required.");
          return;
        }

        const day = document.getElementById("EventDay")?.value;
        if (!day || day.trim() === "") {
          setEventDayDateError("Event Day is required.");
          return;
        }

        const month = document.getElementById("EventMonth")?.value;
        if (!month || month.trim() === "") {
          setEventMonthDateError("Event Month is required.");
          return;
        }

        const year = document.getElementById("EventYear")?.value;
        if (!year || year.trim() === "") {
          setEventYearDateError("Event Year is required.");
          return;
        }

        const starthour = document.getElementById("EventStartHour")?.value || "";
        const startminute = document.getElementById("EventStartMinute")?.value || "";
        const endhour = document.getElementById("EventEndHour")?.value || "";
        const endminute = document.getElementById("EventEndMinute")?.value || "";


        const EventType = document.getElementById("EventType")?.value;
        if (!EventType || EventType.trim() === "") {
          setEventTypeError("Event Type is required. - if unsure select 'Other'.");
          return;
        }

        const EventPrice = document.getElementById("EventPrice")?.value;
        if (!EventPrice || EventPrice.trim() === "") {
          setEventPriceError("Event Price is required. - 0 is free. TBC is unknown");
          return;
        }

        const EventLocation = document.getElementById("EventLocation")?.value;
        if (!EventLocation || EventLocation.trim() === "") {
          setEventLocationError("Event Location is required. - TBC is unknown");
          return;
        }

        const EventPostcode = document.getElementById("EventPostcode")?.value;
        if (!EventPostcode || EventPostcode.trim() === "") {
          setEventPostcodeError("Event Postcode is required. - TBC is unknown");
          return;
        }
        
  

        // Collect event data
        const EVENT_NAME = eventName;
        const EVENT_DATE = day && month && year ? `${day}/${month}/${year}` : "";
        const EVENT_DAYS = "0";
        const EVENT_PRICE = EventPrice;
        const EVENT_TYPE = EventType;
        const EVENT_SITE = document.getElementById("EventSite")?.value || "";
        const EVENT_TICKETSITE = "";
        const EVENT_LOCATION = EventLocation;
        const EVENT_POSTCODE = EventPostcode;
        const EVENT_DESCRIPTION = JSON.stringify(editorValue);

        const EVENT_STARTTIME = starthour && startminute ? `${starthour}:${startminute}` : "";
        const EVENT_ENDTIME = endhour && endminute ? `${endhour}:${endminute}` : "";

        const EVENT_LOCATIONMAP = "";
        const EVENT_OWNER = uid;

        const EVENT_IMAGELOCATION = "BANNER";

        const EVENT_SPONSORED = "N";
        const EVENT_VERIFIED = "N";
        const EVENT_SOLDOUT = "N";

        const SETCATEGORY = document.getElementById("EventCategory")?.value || "AUTOMOTIVE";

        const EVENT_PUBLIC = document.getElementById("EventPublic")?.checked ? "Y" : "N";

        const EventRequestToJoin = document.getElementById("EventRequestToJoin")?.checked ? "Y" : "N";

        const slugify = (text) => {
          return text
            .toLowerCase()                   // lowercase
            .normalize("NFD")                // normalize accented characters
            .replace(/[\u0300-\u036f]/g, "")// remove accents
            .replace(/[^a-z0-9]+/g, "-")    // replace non-alphanumeric with -
            .replace(/(^-|-$)/g, "");       // trim - from start/end
        };

        const EVENT_SLUG = slugify(eventName);

        // Generate unique EVENT_URLKEY
        const EVENT_URLKEY = await generateEventURLKey(database);

        // Create unique ID
        const now = new Date();
        const RaceCreatetime = `EVENTUID${now.getTime()}${Math.floor(Math.random() * 999999)}`;

        const EVENT_PRICEMIN = "";
        const EVENT_PRICEVIPMIN = "";
        const EVENT_PRICEVIPMAX = "";
        const EVENT_PRICETRACKMIN = "";
        const EVENT_PRICETRACKMAX = "";
        const EVENT_PRICEWEEKENDMIN = "";
        const EVENT_PRICEWEEKENDMAX = "";
        const EVENT_SHOWREACTIONS = "N";
  
        const newday = now.getDate();
        const newmonth = now.getMonth() + 1;
        const newyear = now.getFullYear();
        const hour = now.getHours();
        const EVENT_CREATED = `${newday}/${newmonth}/${newyear} ${hour}`;

        const eventData = {
          EVENT_NAME,
          EVENT_DATE,
          EVENT_DAYS,
          EVENT_PRICE,
          EVENT_TYPE,
          EVENT_SITE,
          EVENT_TICKETSITE,
          EVENT_LOCATION,
          EVENT_POSTCODE,
          EVENT_LOCATIONMAP,
          EVENT_STARTTIME,
          EVENT_ENDTIME,
          EVENT_OWNER,
          EVENT_OWNERNAME: EVENT_OWNERDISPLAY,
          EVENT_DESCRIPTION,
          EVENT_IMAGELOCATION,
          EVENT_SPONSORED,
          EVENT_VERIFIED,
          EVENT_SOLDOUT,
          SETCATEGORY,
          EVENT_SLUG,
          EVENT_URLKEY,
          EVENT_PRICEMIN,
          EVENT_PRICEVIPMIN,
          EVENT_PRICEVIPMAX,
          EVENT_PRICETRACKMIN,
          EVENT_PRICETRACKMAX,
          EVENT_PRICEWEEKENDMIN,
          EVENT_PRICEWEEKENDMAX,
          EVENT_SHOWREACTIONS: EVENT_SHOWREACTIONS,
          EVENT_PUBLIC: EVENT_PUBLIC,
          EVENT_ATTENDBYJOINING: EventRequestToJoin,
          EVENT_CREATED: EVENT_CREATED,
          EVENT_PHOTOGRAPHYALLOWED: "N",
          EVENT_DRONESALLOWED: "N"
        };

        // Save to DB
        const eventPath = `EVENTS/EVENT/${SETCATEGORY}/${RaceCreatetime}`;
        await set(ref(database, eventPath), eventData);

        // Hide modal and show success toast
        toast("Your event has been successfully created!", `https://attendiau.co.uk/event/${EVENT_URLKEY}/${EVENT_SLUG}`, "created");

        eventAudit(SETCATEGORY, "CREATE", EVENT_NAME, uid, EVENT_CREATED, RaceCreatetime, "Event Created"); 

      } catch (error) {
        // console.log(error);
        toast("Error creating Event. <br> Please Retry.", "#", "danger");
      }
    });
  };


    function eventAudit(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };

  //   const handleClick = () => {
  //       toast("Event Created!", "success");
  //   };

  return (
    <div className="container my-5">

      {user && (
        <>

        <h1 className="mt-5 event-heading-start">Create New Event.</h1>
        <p className="event-sub-text mb-5"> Create and share your own public event in just a few steps. Simply enter the event name, date, location, and a short description. Once submitted, your event will appear on our public events page, where others can view details, join, and track updates.</p>
                  

          
        <div className="row">
          <div className="col-md-6 mb-3">
        <label htmlFor="EventCategory" className="form-label">Event Category</label>
        <select
          className="form-select"
          id="EventCategory"
          value={category}
          onChange={handleCategoryChange}
        >
          <option value="">Select</option>
          <option value="AUTOMOTIVE">Automotive</option>
          <option value="SPORTS">Sports</option>
          <option value="MUSIC">Music</option>
        </select>
       </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="EventName" className="form-label">
              Event Name <span className="text-danger-small">Required</span>
            </label>
            <input type="text" className={`form-control${eventNameError ? " is-invalid" : ""}`} id="EventName" />
            {eventNameError && <div className="text-danger mt-1">{eventNameError}</div>}
          </div>
        </div>

         <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">Event Date <span className="text-danger-small">Required</span></label>
            <div className="d-flex gap-2">
              <select className={`form-select${eventDayDateError ? " is-invalid" : ""}`} id="EventDay">
                <option value="">Day</option>
                {[...Array(31)].map((_, i) => (
                  <option key={i + 1} value={String(i + 1).padStart(2, "0")}>{i + 1}</option>
                ))}
              </select>

              <select className={`form-select${eventMonthDateError ? " is-invalid" : ""}`} id="EventMonth">
                <option value="">Month</option>
                {["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"].map((m, i) => (
                  <option key={m} value={m}>{new Date(0, i).toLocaleString("en-GB", { month: "long" })}</option>
                ))}
              </select>

              <select className={`form-select${eventYearDateError ? " is-invalid" : ""}`} id="EventYear">
                <option value="">Year</option>
                {Array.from({ length: 2 }, (_, i) => {
                  const year = new Date().getFullYear() + i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>

            {eventDayDateError && <div className="text-danger mt-1">{eventDayDateError}</div>}
            {eventMonthDateError && <div className="text-danger mt-1">{eventMonthDateError}</div>}
            {eventYearDateError && <div className="text-danger mt-1">{eventYearDateError}</div>}
          </div>

          <div className="col-md-2">
            <label className="form-label">Start Time</label>
            <div className="d-flex gap-2">
              <select className="form-select" id="EventStartHour">
                <option value="">Hour</option>
                {[...Array(24)].map((_, i) => (
                  <option key={i} value={String(i).padStart(2, "0")}>{String(i).padStart(2, "0")}</option>
                ))}
              </select>

              <select className="form-select" id="EventStartMinute">
                <option value="">Mins</option>
                {[...Array(60)].map((_, i) => (
                  <option key={i} value={String(i).padStart(2, "0")}>{String(i).padStart(2, "0")}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-md-2">
            <label className="form-label">End Time</label>
            <div className="d-flex gap-2">
              <select className="form-select" id="EventEndHour">
                <option value="">Hour</option>
                {[...Array(24)].map((_, i) => (
                  <option key={i} value={String(i).padStart(2, "0")}>{String(i).padStart(2, "0")}</option>
                ))}
              </select>

              <select className="form-select" id="EventEndMinute">
                <option value="">Mins</option>
                {[...Array(60)].map((_, i) => (
                  <option key={i} value={String(i).padStart(2, "0")}>{String(i).padStart(2, "0")}</option>
                ))}
              </select>
            </div>
          </div>

        </div>

        <div className="row ">
          <div className="col-12 mb-3">
            <label className="form-label">Description</label>
            <div className="editor-wrapper">
              {/* Toolbar */}
              <div className="editor-toolbar">
                <MarkButton  format="bold" label='<i class="bi bi-type-bold"></i>' />
                <MarkButton  format="italic" label='<i class="bi bi-type-italic"></i>' />
                <MarkButton  format="underline" label='<i class="bi bi-type-underline"></i>' />
                <BlockButton format="heading-one" label='<i class="bi bi-type-h1"></i>' />
                <BlockButton format="heading-two" label='<i class="bi bi-type-h2"></i>' />
                <BlockButton format="heading-three" label='<i class="bi bi-type-h3"></i>' />
                <BlockButton format="paragraph" label='<i class="bi bi-paragraph"></i>' />
                <BlockButton format="bulleted-list" label='<i class="bi bi-list-task"></i>' />
                <BlockButton format="numbered-list" label='<i class="bi bi-list-ol"></i>' />
              </div>
              {/* Editor */}
              <div className="editor-container">
                <Slate editor={editor} initialValue={editorValue} onChange={setEditorValue}>
                  <Editable 
                    placeholder="Type your event description here..."
                    style={{ outline: 'none', minHeight: '120px', fontFamily: 'inherit', fontSize: 'inherit' }}
                    renderElement={({ attributes, children, element }) => {
                      switch (element.type) {
                        case 'heading-one':
                          return <h1 {...attributes} style={{ fontSize: '24px', fontWeight: 'bold', margin: '10px 0' }}>{children}</h1>;
                        case 'heading-two':
                          return <h2 {...attributes} style={{ fontSize: '20px', fontWeight: 'bold', margin: '10px 0' }}>{children}</h2>;
                        case 'heading-three':
                          return <h3 {...attributes} style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0'}}>{children}</h3>;
                        case 'bulleted-list':
                          return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'disc' }}>{children}</li>;
                        case 'numbered-list':
                          return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'decimal' }}>{children}</li>;
                        default:
                          return <p {...attributes}>{children}</p>;
                      }
                    }}
                    renderLeaf={({ attributes, children, leaf }) => {
                      if (leaf.bold) children = <strong>{children}</strong>;
                      if (leaf.italic) children = <em>{children}</em>;
                      if (leaf.underline) children = <u>{children}</u>;
                      return <span {...attributes}>{children}</span>;
                    }}
                  />
                </Slate>
              </div>
            </div>
          </div>
        </div>

 <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="EventType" className="form-label">
            Event Type <span className="text-danger-small">Required</span>
          </label>
          <select
            className={`form-select${eventTypeError ? " is-invalid" : ""}`}
            id="EventType"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
          >
            <option value="">Select</option>
            {category &&
              eventOptions[category].map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
          </select>
          {eventTypeError && <div className="text-danger mt-1">{eventTypeError}</div>}
        </div>
      </div>

        <div className="row ">       
          <div className="col-md-6 mb-3">
            <label htmlFor="EventPrice" className="form-label">Event Price <span className="text-danger-small">Required</span></label>
            <input type="text" className={`form-control${eventPriceError ? " is-invalid" : ""}`} id="EventPrice" placeholder="" />
            {eventPriceError && <div className="text-danger mt-1">{eventPriceError}</div>}
          </div>
        </div>

        <div className="row "> 
          <div className="col-md-6 mb-3">
            <label htmlFor="EventSite" className="form-label">Website</label>
            <input type="url" className="form-control" id="EventSite" />
          </div>
        </div>

        <div className="row "> 
          <div className="col-md-6 mb-3">
            <label htmlFor="EventLocation" className="form-label">Location <span className="text-danger-small">Required</span></label>
            <input type="text" className={`form-control${eventLocationError ? " is-invalid" : ""}`} id="EventLocation" />
            {eventLocationError && <div className="text-danger mt-1">{eventLocationError}</div>}
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="EventPostcode" className="form-label">Postcode <span className="text-danger-small">Required</span></label>
            <input type="text" className={`form-control${eventPostcodeError ? " is-invalid" : ""}`} id="EventPostcode" />
            {eventPostcodeError && <div className="text-danger mt-1">{eventPostcodeError}</div>}
          </div>
        </div>

        <div className="row"> 
          <div className="col-md-6 mb-3">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" id="EventPublic" defaultChecked />
              <label className="form-check-label" htmlFor="EventPublic">
                Public Event
              </label>
            </div>
          </div>
        </div>      
    

        <div className="row"> 
          <div className="col-md-6 mb-3">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" id="EventRequestToJoin" />
              <label className="form-check-label" htmlFor="EventRequestToJoin">
                Users have to request to join?
              </label>
            </div>
          </div>
        </div>      

        <div className="row">
          <div className="col-md-12 mb-3 text-end">
            <button type="button" className="btn btn-primary mt-3" onClick={handleSaveEvent}>Create</button>
          </div>
        </div>
          
        </>
      )}

    </div>
  );
};

export default EventCreate;
